import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { firstValueFrom } from 'rxjs';

import { ApiConfiguration } from '@customer-portal/api';
import { downloadFile, getFilenameFromContentDisposition } from '@customer-portal/utils';

import type { DownloadNode } from './contract/uitree.types';

/**
 * Fetches and saves a `download` node's file. Deliberately mirrors
 * `DownloadSelectedInvoicesHandler` (customer-invoicing/data-access): the model-supplied `url` is
 * fetched via `HttpClient` (never a plain `<a href>`/browser navigation), so the same-origin
 * `apiAuthorizationToken` interceptor attaches the customer's bearer token — a raw browser
 * navigation to the API has no way to carry it and would 401.
 *
 * `url` is untrusted (it travels through the LLM's UITree output); it is only ever fetched if it
 * targets the Klantportaal API origin, so a manipulated tool result cannot make the browser send
 * the bearer token, or any other authenticated request, somewhere else.
 */
@Injectable({ providedIn: 'root' })
export class DownloadNodeHandler {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfiguration = inject(ApiConfiguration);

  public async download(node: DownloadNode): Promise<boolean> {
    if (!node.url.startsWith(this.apiConfiguration.rootUrl)) {
      Sentry.logger.error('Refused to fetch a download node URL outside the API origin', { url: node.url });
      return false;
    }

    try {
      const response = await firstValueFrom(this.httpClient.get(node.url, { responseType: 'blob', observe: 'response' }));

      if (!response.body) return false;

      const filename = getFilenameFromContentDisposition(response.headers.get('Content-Disposition')) ?? node.label;

      return downloadFile(response.body, filename);
    } catch (error: unknown) {
      const statusCode = error instanceof HttpErrorResponse ? error.status : undefined;
      Sentry.logger.error('Chat download node failed', { url: node.url, statusCode });
      return false;
    }
  }
}
